<template>
  <div class="lib__form__main">
    <div class="lib__form__main__content" v-if="isNotScheduledElectricityCutoff">
        <ValidationObserver slim ref="validator">
          <Feedback outline level="success">
            <p v-html="$t('complaint.isNotScheduledElectricityCutoff.part1')"></p>
            <p v-html="$t('complaint.isNotScheduledElectricityCutoff.part2')"></p>
            <p>
              <ul>
                <li v-html="$t('complaint.isNotScheduledElectricityCutoff.part3')"></li>
                <li v-html="$t('complaint.isNotScheduledElectricityCutoff.part4')"></li>
                <li v-html="$t('complaint.isNotScheduledElectricityCutoff.part5')"></li>
              </ul>
            </p>
        </Feedback>
      </ValidationObserver>
    </div>

    <div class="lib__form__main__content" v-else>
      <header class="lib__form__main__header">
        <h2 class="lib__h2">{{ identificationTitle }}</h2>
      </header>
      <ValidationProvider rules="required" slim v-slot="{ errors, passed, failed }" v-if="!eanRequired">
        <div :class="[failed && 'lib__field--error', passed && 'lib__field--valid']" class="lib__field">
          <div class="lib__tiles lib__tiles--inline">
            <TileRadio
              item="ean"
              key="ean"
              :label="$t('complaint.identification.byEan')"
              group="identification"
              v-model="identificationType"
            >
            </TileRadio>

            <TileRadio
              item="address"
              key="address"
              :label="addressLabel"
              group="identification"
              v-model="identificationType"
            >
            </TileRadio>
          </div>
          <error-message :error="errors[0]"></error-message>
        </div>
      </ValidationProvider>

      <ValidationObserver slim ref="validator">
        <identification-by-ean
          @canContinue="emitCanContinue($event)"
          @eanCode="eanCode = $event"
          @eanInfo="eanInformation = $event"
          :eanCode="eanCode"
          :eanInfo="eanInformation"
          v-if="identificationType === 'ean'"
        ></identification-by-ean>
        <address-selector
          :oresAddressOnly="oresAddressOnly"
          :hideSuccessMessages="false"
          :showSynergridLink="false"
          :numberRequired="numberRequired"
          :streetRequired="streetRequired"
          v-if="identificationType === 'address'"
          :checkForElec="oresAddressOnly"
          :checkForGas="oresAddressOnly"
          :data-vv-as="$t('common.address.address')"
          :hideMap="true"
          name="address"
          v-model="address"
          @elecOrGasAllowed="emitCanContinue"
        ></address-selector>
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import Step from '@/components/steps/Step'
import IdentificationByEan from '@/components/inputs/complaint/identification/IdentificationByEan.vue'
import AddressSelector from '@/components/inputs/address/AddressSelector.vue'
import Component from 'vue-class-component'
import { AddressPoint, CategorySelectionStepModel, GridAccessPoint, IdentificationModel } from '@/models'
import { Mutation, State } from 'vuex-class'
import { complaintService, helper } from '@/services'
import { Emit, Prop } from 'vue-property-decorator'

@Component({
  name: 'identification-step',
  components: {
    IdentificationByEan,
    AddressSelector,
  },
})
export default class IdentificationStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, required: false, default: false }) public eanRequired!: boolean
  //#endregion

  //#region [Data]
  @State('identification', { namespace: 'complaint/identification' })
  public identificationState!: IdentificationModel | null
  @State('requestId', { namespace: 'complaint' })
  public requestId!: string
  @State('category', { namespace: 'complaint/categorySelection' })
  public categorySelectionState!: CategorySelectionStepModel | null

  public identificationType: string | null = null
  public address: AddressPoint | null = null
  public eanCode: string | null = null
  public eanInformation: GridAccessPoint | null = null
  //#endregion

  //#region [Mutation]
  @Mutation('STORE', { namespace: 'complaint/identification' })
  private store!: (index: IdentificationModel | null) => void
  //#endregion

  //#region [Computed]
  public get isNotScheduledElectricityCutoff(): boolean {
    return (
      !!this.categorySelectionState &&
      !!this.categorySelectionState.category &&
      this.categorySelectionState.category.key === 'notScheduledElectricityCutoff'
    )
  }

  public get oresAddressOnly(): boolean {
    return complaintService.isInList(this.categorySelectionState, complaintService.oresAddresOnlyList)
  }

  public get identificationTitle(): string {
    return this.eanRequired
      ? `${this.$t('complaint.identification.title')} ${this.$t('complaint.identification.byEan')}`
      : this.$t('complaint.identification.title').toString()
  }

  public get numberRequired(): boolean {
    return !complaintService.isInList(this.categorySelectionState, complaintService.numberNotRequiredList)
  }

  public get streetRequired(): boolean {
    return !complaintService.isInList(this.categorySelectionState, complaintService.streetNotRequiredList)
  }

  public get addressLabel(): string {
    if (complaintService.isInList(this.categorySelectionState, complaintService.consumptionAddressLabelList)) {
      return this.$t('complaint.identification.byConsumptionAddress').toString()
    }
    if (complaintService.isInList(this.categorySelectionState, complaintService.complaintAddressLabelList)) {
      return this.$t('complaint.identification.byComplaintAddress').toString()
    }
    if (complaintService.isInList(this.categorySelectionState, complaintService.addressLabelList)) {
      return this.$t('complaint.identification.byAddress').toString()
    }
    return ''
  }
  //#endregion

  //#region [Method]
  public mounted() {
    if (this.isNotScheduledElectricityCutoff) {
      this.emitCanContinue(false)
    }

    if (this.identificationState) {
      this.identificationType = helper.clone(this.identificationState.identificationType)
      this.address = helper.clone(this.identificationState.address)
      this.eanCode = helper.clone(this.identificationState.eanCode)
      this.eanInformation = helper.clone(this.identificationState.eanInfo)
    }

    if (this.eanRequired) {
      this.identificationType = 'ean'
      this.address = null
    }
  }

  public async save(next: boolean, isValid: boolean) {
    if (next && isValid && this.identificationType) {
      this.$appInsights.trackEvent('Complaint_StepIdentification_Validated', {
        Segment: 'Complaint',
        WebRef: this.requestId,
        IdentificationType: this.identificationType,
      })
    }
    this.store(
      this.identificationType === 'ean'
        ? {
            identificationType: this.identificationType,
            address: null,
            eanCode: this.eanCode,
            eanInfo: this.eanInformation,
          }
        : {
            identificationType: this.identificationType,
            address: this.address,
            eanCode: null,
            eanInfo: null,
          },
    )
  }

  @Emit('canContinue')
  public emitCanContinue(value: boolean) {
    return value
  }
  //#endregion
}
</script>